import AuthorizationApi from '@/api/authorization';
import { authDialog } from '@/components/auth-dialog';
import { AgreementPo } from '@/po/agreement';
import AgreementApi, { generalQueryRegisterAgrees } from '@/api/agreement';
import { RegisterUserAgreement } from '@/api/agreement/types';
import { ubtSendError } from '@/shared/utils/ubt';
import { ubtSetupPageButton } from '@/shared/utils/ubt/ubt-setup';

interface AgreementsCache {
  financeAgreements: AgreementPo[];
  platformAgreements: RegisterUserAgreement[];
  isConfirmed: boolean;
}

class ConfirmAgreementsService {
  private agreementsCache: AgreementsCache = {
    financeAgreements: [],
    platformAgreements: [],
    isConfirmed: false,
  };

  private initAgreementsTaskCache: Promise<[AgreementPo[], RegisterUserAgreement[]]> | null = null;

  /**
   * 获取金融协议列表
   */
  private async getFinanceAgreements(): Promise<AgreementPo[]> {
    const financeAgreementsData = await generalQueryRegisterAgrees() || {};

    this.agreementsCache.financeAgreements = financeAgreementsData.agreements || [];

    return this.agreementsCache.financeAgreements;
  }

  /**
   * 获取平台注册协议列表
   */
  private async getPlatformAgreements(): Promise<RegisterUserAgreement[]> {
    const platformAgreementsData = await AgreementApi.getWaitRegisterAgrees();

    this.agreementsCache.platformAgreements = platformAgreementsData || [];

    return this.agreementsCache.platformAgreements;
  }

  /**
   * 初始化协议数据
   */
  private async initAgreementsData(): Promise<[AgreementPo[], RegisterUserAgreement[]]> {
    if (!this.initAgreementsTaskCache) {
      this.initAgreementsTaskCache = new Promise(async (resolve, reject) => {
        try {
          const [financeAgreementsData, platformAgreementsData] = await Promise.all([
            this.getFinanceAgreements(),
            this.getPlatformAgreements(),
          ]);

          /** 若协议数据为空，则标识已同意过协议 */
          this.agreementsCache.isConfirmed = !financeAgreementsData?.length
            && !platformAgreementsData?.length;

          resolve([financeAgreementsData, platformAgreementsData]);
        } catch (error) {
        /** 服务接口异常，清除缓存 */
          this.initAgreementsTaskCache = null;
          reject(error);
        }
      });
    }

    return this.initAgreementsTaskCache;
  }

  /**
   * 预加载协议数据（对外使用，异常已进行try-catch兜底）
   */
  public async preloadAgreementsData(): Promise<void> {
    try {
      /** 清空缓存数据，避免更新不及时问题 */
      this.initAgreementsTaskCache = null;

      await this.initAgreementsData();
    } catch (error) {
      ubtSendError({
        name: 'preloadAgreementsData error',
        message: (error as Error)?.message,
      });
    }
  }

  /**
   * 确认并同意协议
   * @returns {Promise<boolean>} 是否同意协议成功
   */
  public async confirmFinanceAgreements(): Promise<boolean> {
    try {
      /** 埋点上报-与历史保持一致 */
      ubtSetupPageButton('finance_agreeFinanceAgreements');

      /** 初始化协议数据 */
      const [financeAgreements, platformAgreements] = await this.initAgreementsData();

      /** 若已同意过协议，则直接返回true */
      if (this.agreementsCache.isConfirmed) return true;

      try {
        /** 同意协议弹窗 */
        await authDialog.open(financeAgreements);
      } catch (error) {
        /** 同意协议失败，返回false，中断主流程 */
        return false;
      }

      const submitAgreeTask = [];

      /** 提交保存-同意金融协议 */
      if (financeAgreements?.length) {
        submitAgreeTask.push(
          AuthorizationApi.agree(financeAgreements?.map((agreement) => agreement.id)),
        );
      }

      /** 提交保存-同意平台注册协议 */
      if (platformAgreements?.length) {
        submitAgreeTask.push(
          AgreementApi.markUserAgreements(platformAgreements?.map((it) => it.agreementId || '')),
        );
      }

      /** 提交保存-同意留痕金融协议、平台协议 */
      await Promise.all(submitAgreeTask);

      /** 若同意留痕成功，则更新标识 */
      this.agreementsCache.isConfirmed = true;

      return true;
    } catch (error) {
      ubtSendError(error as Error);
      /** 因服务接口异常，不阻塞主流程 */
      return true;
    }
  }
}

/** 协议服务实例 */
export const agreementService = new ConfirmAgreementsService();
